import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "bizpandit-ui";
import { formatDateTime } from "@/lib/date";
import { KIND_LABEL, KIND_TONE, notificationsService, type NotificationDto } from "./notifications.service";

/**
 * The bell — Dashboard spec §6.1. It rendered an icon and read nothing until
 * now, with a permanent red dot that meant no more than the icon did.
 *
 * The badge polls one cheap COUNT rather than the list, and the list is only
 * fetched when the menu opens. Opening an item marks that one read and
 * navigates to its link; there is a "mark all read" for the rest.
 */
export default function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const countQ = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => notificationsService.unreadCount(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const listQ = useQuery({
    queryKey: ["notifications", "inbox"],
    queryFn: () => notificationsService.inbox(false, 20),
    enabled: open,
  });

  const read = useMutation({
    mutationFn: (ids?: string[]) => notificationsService.markRead(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unread = countQ.data?.unread ?? 0;
  const items = listQ.data?.items ?? [];

  const openItem = (n: NotificationDto) => {
    if (!n.read_on) read.mutate([n.notification_id]);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative rounded-lg p-2 text-navy2/70 transition-colors hover:bg-surface hover:text-navy2"
          aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 min-w-[17px] rounded-full bg-rose px-1 text-[10px] font-bold leading-[17px] text-roseink">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between border-b border-linegray px-3 py-2">
          <span className="text-[13px] font-semibold text-navy">Notifications</span>
          {unread > 0 && (
            <button type="button" className="text-[11.5px] font-semibold text-rose hover:underline" onClick={() => read.mutate(undefined)} disabled={read.isPending}>
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {listQ.isLoading && <p className="m-0 px-3 py-6 text-center text-[12.5px] text-navy2/60">Loading…</p>}
          {!listQ.isLoading && items.length === 0 && (
            <p className="m-0 px-3 py-6 text-center text-[12.5px] text-navy2/60">Nothing yet. A task assigned to you, a version to review, or a deadline coming up will appear here.</p>
          )}
          {items.map((n) => (
            <button
              key={n.notification_id}
              type="button"
              onClick={() => openItem(n)}
              className={`block w-full border-b border-linegray px-3 py-2 text-left transition-colors hover:bg-surface ${n.read_on ? "" : "bg-rosepale/40"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-[12.5px] font-semibold text-navy">{n.title}</span>
                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold ${KIND_TONE[n.kind] ?? ""}`}>{KIND_LABEL[n.kind] ?? n.kind}</span>
              </div>
              {n.body && <div className="mt-0.5 text-[11.5px] text-navy2/70">{n.body}</div>}
              <div className="mt-0.5 text-[10.5px] text-navy2/50">{formatDateTime(n.created_on)}</div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
